import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateSolarPricingInputSteps } from "./solar-price-dependents"
import type {
  SolarProposalUtilityDto,
  UpdateSolarProposalUtilityDto,
} from "../dto/solar-proposal-utility.dto"
import { solarProposalService } from "../services/solar-proposal.service"

export type UpdateSolarProposalUtilityVariables = {
  proposalId: string
  body: UpdateSolarProposalUtilityDto
}

/**
 * Saves the utility selection. The answer is the utility read itself, so it
 * replaces that cache entry; a failure refetches it. Nothing recalculates: the savings and pricing
 * reads are refetched so they report out of date, and the header and design
 * reads so they name the new utility.
 */
export default function useUpdateSolarProposalUtility(): UseMutationResult<
  SolarProposalUtilityDto,
  unknown,
  UpdateSolarProposalUtilityVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, body }: UpdateSolarProposalUtilityVariables) =>
      solarProposalService.putUtility(proposalId, body),
    onSuccess: async (utility, { proposalId }) => {
      queryClient.setQueryData(
        queryKeys.solarProposal.utility(proposalId),
        utility,
      )
      await Promise.all(
        [
          queryKeys.solarProposal.savings(proposalId),
          queryKeys.solarProposal.pricing(proposalId),
          queryKeys.solarProposal.systemPricing(proposalId),
          queryKeys.solarProposal.design(proposalId),
          queryKeys.solarProposal.share(proposalId),
        ].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey, exact: true }),
        ),
      )
      for (const queryKey of [
        queryKeys.solarProposal.byId(proposalId),
        // Its pricing and savings freshness blockers.
        queryKeys.solarProposal.contract(proposalId),
      ]) {
        void queryClient.invalidateQueries({ queryKey, exact: true })
      }
      // The gateway builds the equipment and programs for the utility rate, so
      // every cached Finance Partner & Equipment preview is for the old one.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.productOffering(proposalId),
      })
      // Adders, utility rebates and VPP programs depend on the utility.
      invalidateSolarPricingInputSteps(queryClient, proposalId)
    },
    // A lock that appeared, or an account that couldn't be set back, changes
    // what the form may do; the seller's draft is local and survives.
    onError: (_error, { proposalId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.utility(proposalId),
        exact: true,
      })
    },
  })
}
